import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Store, Check, ArrowRight, ShieldCheck } from "lucide-react";

export default function VendorRegister() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    tagline: "",
    description: "",
    logo: "",
    banner: "",
    instagram: "",
    website: "",
    contact_email: "",
  });

  useEffect(() => {
    document.title = "Become a PFC Vendor — Marketplace";
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/vendor/register")}`);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/vendor/me");
        if (data.vendor) {
          setVendor(data);
          setForm({
            business_name: data.vendor.business_name || "",
            tagline: data.vendor.tagline || "",
            description: data.vendor.description || "",
            logo: data.vendor.logo || "",
            banner: data.vendor.banner || "",
            instagram: data.vendor.instagram || "",
            website: data.vendor.website || "",
            contact_email: data.vendor.contact_email || user.email || "",
          });
        } else {
          setForm((s) => ({ ...s, contact_email: user.email }));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user, nav]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/vendor/register", form);
      toast.success("Vendor profile saved. Proceed to activation payment.");
      const { data } = await api.get("/vendor/me");
      setVendor(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to save vendor profile.");
    } finally {
      setSaving(false);
    }
  };

  const payActivation = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/vendor/checkout/activation");
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not start payment.");
      setPaying(false);
    }
  };

  const paySubscribe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/vendor/checkout/subscribe");
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not start payment.");
      setPaying(false);
    }
  };

  if (loading) return <div className="bg-ink-900 min-h-screen py-24 text-center text-white/40">Loading…</div>;

  const v = vendor?.vendor;
  const step = !v ? 1 : !v.activation_paid ? 2 : !v.subscription_active ? 3 : 4;

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="vendor-register">
      <div className="max-w-3xl mx-auto px-5 py-14">
        <div className="text-pfc-gold text-[11px] tracking-[0.4em] uppercase font-sub mb-3 flex items-center gap-2">
          <Store size={14} /> Vendor Registration
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Open Your PFC Storefront</h1>
        <p className="text-white/70 mt-4 max-w-2xl">
          List unlimited products. Reach the PFC community. <span className="text-pfc-gold">$99 one-time activation</span> + <span className="text-pfc-gold">$9.99/month</span> to keep your storefront live.
        </p>

        {/* Stepper */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10" data-testid="vendor-stepper">
          <StepBadge num={1} label="Business Info" done={step > 1} active={step === 1} />
          <StepBadge num={2} label="$99 Activation" done={step > 2} active={step === 2} />
          <StepBadge num={3} label="$9.99 / mo" done={step > 3} active={step === 3} />
        </div>

        {step === 4 && (
          <div className="mt-10 border border-pfc-gold/40 bg-pfc-gold/5 p-6 flex items-start gap-4" data-testid="vendor-active-banner">
            <Check className="text-pfc-gold mt-1" />
            <div>
              <div className="font-display text-xl text-white">Your storefront is live.</div>
              <p className="text-white/70 text-sm mt-1">Manage products and orders from your dashboard.</p>
              <Link to="/vendor/dashboard" className="inline-flex items-center gap-1 text-pfc-gold text-xs font-sub uppercase tracking-[0.3em] mt-3 hover:underline">
                Open Dashboard <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        )}

        {/* Step 1 — Business form */}
        {step <= 1 || (v && step === 1) ? (
          <form onSubmit={submit} className="mt-10 space-y-4 border border-white/10 p-6" data-testid="vendor-form">
            <Input label="Business Name" required value={form.business_name} onChange={(v) => update("business_name", v)} testId="biz-name" />
            <Input label="Tagline" value={form.tagline} onChange={(v) => update("tagline", v)} placeholder="One-line pitch about your brand" testId="biz-tagline" />
            <Textarea label="Description" value={form.description} onChange={(v) => update("description", v)} placeholder="Tell shoppers about your brand, materials, ethos…" testId="biz-desc" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Logo URL" value={form.logo} onChange={(v) => update("logo", v)} testId="biz-logo" />
              <Input label="Banner URL (optional)" value={form.banner} onChange={(v) => update("banner", v)} testId="biz-banner" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Instagram (@handle)" value={form.instagram} onChange={(v) => update("instagram", v)} testId="biz-ig" />
              <Input label="Website" value={form.website} onChange={(v) => update("website", v)} testId="biz-web" />
            </div>
            <Input label="Contact Email" required type="email" value={form.contact_email} onChange={(v) => update("contact_email", v)} testId="biz-email" />
            <button
              type="submit"
              disabled={saving}
              data-testid="save-vendor-btn"
              className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-60"
            >
              {saving ? "Saving…" : v ? "Update & Continue" : "Save & Continue →"}
            </button>
          </form>
        ) : null}

        {/* Step 2 — Activation */}
        {step === 2 && (
          <div className="mt-8 border border-pfc-gold/40 p-6" data-testid="step-activation">
            <div className="text-pfc-gold text-xs tracking-[0.3em] uppercase font-sub mb-2">Step 2 of 3</div>
            <h3 className="font-display text-2xl">$99 Storefront Activation</h3>
            <p className="text-white/70 text-sm mt-2">One-time fee. Unlocks your seller dashboard, product listing, and order management. After this, you'll set up the $9.99 / month subscription.</p>
            <button
              onClick={payActivation}
              disabled={paying}
              data-testid="pay-activation-btn"
              className="mt-5 w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-60"
            >
              {paying ? "Redirecting…" : "Pay $99 Activation →"}
            </button>
            <div className="mt-4 text-xs text-white/50 flex items-center gap-2">
              <ShieldCheck size={12} className="text-pfc-gold" /> Secure Stripe checkout.
            </div>
          </div>
        )}

        {/* Step 3 — Subscribe */}
        {step === 3 && (
          <div className="mt-8 border border-pfc-gold/40 p-6" data-testid="step-subscribe">
            <div className="text-pfc-gold text-xs tracking-[0.3em] uppercase font-sub mb-2">Step 3 of 3</div>
            <h3 className="font-display text-2xl">$9.99 Monthly Subscription</h3>
            <p className="text-white/70 text-sm mt-2">Keeps your storefront live. Cancel any time — your products go offline immediately at cancellation.</p>
            <button
              onClick={paySubscribe}
              disabled={paying}
              data-testid="pay-subscribe-btn"
              className="mt-5 w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-60"
            >
              {paying ? "Redirecting…" : "Start $9.99 / month →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepBadge({ num, label, done, active }) {
  return (
    <div className={`border p-4 flex items-center gap-3 ${done ? "bg-pfc-gold/10 border-pfc-gold/40" : active ? "border-pfc-gold" : "border-white/10"}`}>
      <div className={`w-8 h-8 grid place-items-center rounded-full ${done ? "bg-pfc-gold text-ink-900" : active ? "bg-pfc-gold/20 text-pfc-gold" : "bg-white/5 text-white/40"}`}>
        {done ? <Check size={16} /> : num}
      </div>
      <div className="text-xs uppercase tracking-[0.25em] font-sub text-white/80">{label}</div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-pfc-gold focus:outline-none"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, required, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <textarea
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={4}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-pfc-gold focus:outline-none resize-y"
      />
    </label>
  );
}
